import { createAsyncThunk, createSlice, PayloadAction, isAnyOf } from '@reduxjs/toolkit'
import BigNumber from 'bignumber.js'
import keyBy from 'lodash/keyBy'
import poolsConfig, { POOL_EMISSIONS } from 'config/constants/pools'
import {
  PoolsState,
  SerializedPool,
  SerializedVaultFees,
  SerializedCakeVault,
  SerializedLockedVaultUser,
  PublicIfoData,
  SerializedVaultUser,
  SerializedLockedCakeVault,
  VaultKey,
} from 'state/types'
import { getPoolApr } from 'utils/apr'
import { BIG_ZERO } from '@pancakeswap/utils/bigNumber'
import cakeAbi from 'config/abi/cake.json'
import { getCakeVaultAddress } from 'utils/addressHelpers'
import { multicallv2 } from 'utils/multicall'
import { findToken, tokens } from '@pancakeswap/tokens'
import { isAddress } from 'utils'
import { getBalanceNumber } from '@pancakeswap/utils/formatBalance'
import { ChainConfig, ChainId, NATIVE } from '@pancakeswap/sdk'
import { createFarmFetcher } from '@pancakeswap/farms'
import { getFarmsPriceHelperLpFiles } from 'config/constants/priceHelperLps/index'
import { getFarms } from '@pancakeswap/farms/constants/common'
import { equalsIgnoreCase } from '@pancakeswap/utils/equalsIgnoreCase'
import {
  fetchPoolsBlockLimits,
  // fetchPoolsBlockLimits,
  // fetchPoolsProfileRequirement,
  fetchPoolsStakingLimits,
  fetchPoolsTotalStaking,
} from './fetchPools'
import {
  fetchPoolsAllowance,
  fetchUserBalances,
  fetchUserPendingRewards,
  fetchUserStakeBalances,
} from './fetchPoolsUser'

import { fetchPublicVaultData, fetchVaultFees, fetchPublicFlexibleSideVaultData, fetchPublicVaultDatas } from './fetchVaultPublic'
import { getTokenPricesFromFarm } from './helpers'
import { resetUserState } from '../global/actions'
import { fetchUserIfoCredit, fetchPublicIfoData } from './fetchUserIfo'
import { fetchVaultUser, fetchFlexibleSideVaultUser, fetchUserVaultDatas } from './fetchVaultUser'


export const initialPoolVaultState = Object.freeze({
  totalShares: null,
  totalLockedAmount: null,
  pricePerFullShare: null,
  totalCakeInVault: null,
  fees: {
    performanceFee: null,
    withdrawalFee: null,
    withdrawalFeePeriod: null,
  },
  userData: {
    isLoading: true,
    userShares: null,
    lastUserActionAmount: null,
    lastDepositedTime: null,
    lastUserActionTime: null,
    pendingProfit: null,
    credit: null,
    locked: null,
    lockStartTime: null,
    lockEndTime: null,
    userBoostedShare: null,
    lockedAmount: null,
    currentOverdueFee: null,
    currentPerformanceFee: null,
  },
  creditStartBlock: null,
})

export const initialIfoState = Object.freeze({
  credit: null,
  ceiling: null,
})

const initialState: PoolsState = {
  data: [...poolsConfig],
  userDataLoaded: false,
  ifo: initialIfoState,
  cakeVault: initialPoolVaultState,
  cakeFlexibleSideVault: initialPoolVaultState,
}

export const fetchCakePoolPublicDataAsync = (chainId: ChainId) => async (dispatch, getState) => {
  const farmsData = getState().farms.data

  const prices = getTokenPricesFromFarm(farmsData)

  const cakePool = poolsConfig.filter((p) => p.sousId === 0)[0]
  const stakingToken = findToken(cakePool.stakingToken.symbol, chainId)
  const stakingTokenAddress = isAddress(stakingToken.address)
  const stakingTokenPrice = stakingTokenAddress ? prices[stakingTokenAddress] : 0
  const earningToken = findToken(cakePool.earningToken.symbol, chainId)
  const earningTokenAddress = isAddress(earningToken.address)
  const earningTokenPrice = earningTokenAddress ? prices[earningTokenAddress] : 0

  dispatch(
    setPoolPublicData({
      sousId: 0,
      data: {
        stakingTokenPrice,
        earningTokenPrice,
      },
    }),
  )
}

export const fetchCakePoolUserDataAsync = (account: string, chainId: ChainId) => async (dispatch) => {
  const cakeVaultAddress = getCakeVaultAddress(chainId)
  const allowanceCall = {
    address: tokens[chainId].cake.address,
    name: 'allowance',
    params: [account, cakeVaultAddress],
  }
  const balanceOfCall = {
    address: tokens[chainId].cake.address,
    name: 'balanceOf',
    params: [account],
  }
  const cakeContractCalls = [allowanceCall, balanceOfCall]
  const [[allowance], [stakingTokenBalance]] = await multicallv2({ abi: cakeAbi, calls: cakeContractCalls, chainId })

  dispatch(
    setPoolUserData({
      sousId: 0,
      data: {
        allowance: new BigNumber(allowance.toString()).toJSON(),
        stakingTokenBalance: new BigNumber(stakingTokenBalance.toString()).toJSON(),
      },
    }),
  )
}

export const fetchPoolsPublicDataAsync =
  (currentBlockNumber: number, chainId: number) => async (dispatch) => {
    try {
      // const blockLimits = []
      const [blockLimits, totalStakings] = await Promise.all([
        fetchPoolsBlockLimits(chainId),
        fetchPoolsTotalStaking(chainId),
      ])
      const livePools = poolsConfig.filter(poolConfig => !!poolConfig.contractAddress[chainId])
      const blockLimitsSousIdMap = keyBy(blockLimits, 'sousId')
      // const totalStakings = fetchPoolsTotalStaking(chainId)
      const totalStakingsSousIdMap = keyBy(totalStakings, 'sousId')

      const farmFetcher = createFarmFetcher(multicallv2)
      const farmsConfig = await getFarms(chainId)
      const priceHelperLpsConfig = getFarmsPriceHelperLpFiles(chainId)
      const farmsData = farmsConfig.concat(priceHelperLpsConfig)
      livePools.forEach(_pool => {
        const lpAddressA = ChainConfig[chainId][`${NATIVE[chainId].symbol}_${_pool.stakingToken.symbol}`] ?? ChainConfig[chainId][`${_pool.stakingToken.symbol}_${NATIVE[chainId].symbol}`]
        if(lpAddressA && farmsData.filter(_farm => equalsIgnoreCase(_farm.lpAddress, lpAddressA)).length===0)
          farmsData.push({
            pid: null,
            lpAddress: lpAddressA,
            lpSymbol: '',
            quoteToken: tokens[chainId].weth,
            token: _pool.stakingToken,
            vaultPid: null,
          })
        const lpAddressB = ChainConfig[chainId][`${NATIVE[chainId].symbol}_${_pool.earningToken.symbol}`] ?? ChainConfig[chainId][`${_pool.earningToken.symbol}_${NATIVE[chainId].symbol}`]
        if(lpAddressB && farmsData.filter(_farm => equalsIgnoreCase(_farm.lpAddress, lpAddressB)).length===0)
          farmsData.push({
            pid: null,
            lpAddress: lpAddressB,
            lpSymbol: '',
            quoteToken: tokens[chainId].weth,
            token: _pool.earningToken,
            vaultPid: null,
          })
      })
      const { farmsWithPrice } = await farmFetcher.fetchFarms({
        chainId,
        farms: farmsData
      })
      const prices = getTokenPricesFromFarm(farmsWithPrice)
      const liveData = livePools.map((pool) => {
        const blockLimit = blockLimitsSousIdMap[pool.sousId]
        const totalStaking = totalStakingsSousIdMap[pool.sousId]
        const isPoolFinished = pool.isFinished // || isPoolEndBlockExceeded

        const stakingToken = findToken(pool.stakingToken.symbol, chainId)
        const stakingTokenAddress = isAddress(stakingToken.address)
        const stakingTokenPrice = stakingTokenAddress ? prices[stakingTokenAddress] : 0

        const earningToken = findToken(pool.earningToken.symbol, chainId)
        const earningTokenAddress = isAddress(earningToken.address)
        const earningTokenPrice = earningTokenAddress ? prices[earningTokenAddress] : 0
        const apr = !isPoolFinished
          ? getPoolApr(
              stakingTokenPrice,
              earningTokenPrice,
              getBalanceNumber(new BigNumber(totalStaking.totalStaked), pool.stakingToken.decimals),
              pool.vaultKey ? parseFloat(pool.tokenPerBlock) : (POOL_EMISSIONS[chainId]?.[stakingToken.symbol] ?? parseFloat(pool.tokenPerBlock)),
            )
          : 0

        return {
          ...blockLimit,
          ...totalStaking,
          profileRequirement: undefined,
          stakingTokenPrice,
          earningTokenPrice,
          apr,
          isFinished: isPoolFinished,
        }
      })
      dispatch(setPoolsPublicData(liveData))
    } catch (error) {
      console.error('[Pools Action] error when getting public data', error)
    }
  }

export const fetchPoolsStakingLimitsAsync = (chainId: ChainId) => async (dispatch, getState) => {
  const poolsWithStakingLimit = getState()
    .pools.data.filter(({ stakingLimit }) => stakingLimit !== null && stakingLimit !== undefined)
    .map((pool) => pool.sousId)

  try {
    const stakingLimits = await fetchPoolsStakingLimits(poolsWithStakingLimit, chainId)

    const stakingLimitData = poolsConfig.map((pool) => {
      if (poolsWithStakingLimit.includes(pool.sousId)) {
        return { sousId: pool.sousId }
      }
      const { stakingLimit, numberBlocksForUserLimit } = stakingLimits[pool.sousId] || {
        stakingLimit: BIG_ZERO,
        numberBlocksForUserLimit: 0,
      }
      return {
        sousId: pool.sousId,
        stakingLimit: stakingLimit.toJSON(),
        numberBlocksForUserLimit,
      }
    })

    dispatch(setPoolsPublicData(stakingLimitData))
  } catch (error) {
    console.error('[Pools Action] error when getting staking limits', error)
  }
}

export const fetchPoolsUserDataAsync = createAsyncThunk<
  { sousId: number; allowance: any; stakingTokenBalance: any; stakedBalance: any; pendingReward: any }[],
  { account: string, chainId: ChainId }
>('pool/fetchPoolsUserData', async ({ account, chainId }, { rejectWithValue }) => {
  try {
    const [allowances, stakingTokenBalances, stakedBalances, pendingRewards] = await Promise.all([
      fetchPoolsAllowance(account, chainId),
      fetchUserBalances(account, chainId),
      fetchUserStakeBalances(account, chainId),
      fetchUserPendingRewards(account, chainId),
    ])
    const userData = poolsConfig.map((pool) => ({
      sousId: pool.sousId,
      allowance: allowances[pool.sousId],
      stakingTokenBalance: stakingTokenBalances[pool.sousId],
      stakedBalance: stakedBalances[pool.sousId] ?? '0',
      pendingReward: pendingRewards[pool.sousId] ?? '0',
    }))
    return userData
  } catch (e) {
    return rejectWithValue(e)
  }
})

export const updateUserAllowance = createAsyncThunk<
  { sousId: number; field: string; value: any },
  { sousId: number; account: string;  chainId: ChainId }
>('pool/updateUserAllowance', async ({ sousId, account, chainId }) => {
  const allowances = await fetchPoolsAllowance(account, chainId)
  return { sousId, field: 'allowance', value: allowances[sousId] }
})

export const updateUserBalance = createAsyncThunk<
  { sousId: number; field: string; value: any },
  { sousId: number; account: string; chainId: ChainId }
>('pool/updateUserBalance', async ({ sousId, account, chainId }) => {
  const tokenBalances = await fetchUserBalances(account, chainId)
  return { sousId, field: 'stakingTokenBalance', value: tokenBalances[sousId] }
})

export const updateUserStakedBalance = createAsyncThunk<
  { sousId: number; field: string; value: any },
  { sousId: number; account: string; chainId: ChainId }
>('pool/updateUserStakedBalance', async ({ sousId, account, chainId }) => {
  const stakedBalances = await fetchUserStakeBalances(account, chainId)
  return { sousId, field: 'stakedBalance', value: stakedBalances[sousId] }
})

export const updateUserPendingReward = createAsyncThunk<
  { sousId: number; field: string; value: any },
  { sousId: number; account: string; chainId: ChainId }
>('pool/updateUserPendingReward', async ({ sousId, account, chainId }) => {
  const pendingRewards = await fetchUserPendingRewards(account, chainId)
  return { sousId, field: 'pendingReward', value: pendingRewards[sousId] }
})

export const fetchVaultPublicData = createAsyncThunk<
  { [vaultKey in VaultKey]?: SerializedLockedCakeVault | SerializedCakeVault }, 
  { chainId: ChainId }
>(
  'vaults/fetchPublicData',
  async ({chainId}) => {
    const result = await fetchPublicVaultDatas(chainId)
    return result
  },
)

export const fetchVaultUserData = createAsyncThunk<
  { [vaultKey in VaultKey]?: SerializedLockedVaultUser | SerializedVaultUser }, 
  { account: string, chainId: ChainId }
>(
  'vaults/fetchUserData',
  async ({account, chainId}) => {
    const result = await fetchUserVaultDatas(account, chainId)
    return result
  },
)

export const fetchVaultFeeDatas = createAsyncThunk<
  { [vaultKey in VaultKey]?: SerializedVaultFees }, 
  { chainId: ChainId }
>('vaults/fetchFees', async ({chainId}) => {
  const vaultFees = await fetchVaultFees(chainId)
  return vaultFees
})

export const fetchLockedVaultPublicData = createAsyncThunk<
  { vaultKey:VaultKey; value: SerializedLockedCakeVault },
  { vaultKey:VaultKey; contractAddress: string; chainId: ChainId }
>(
  'locked/fetchPublicData',
  async ({vaultKey, contractAddress, chainId}) => {
    const value = await fetchPublicVaultData(chainId, contractAddress)
    return { vaultKey, value }
  },
)

export const fetchFlexibleVaultPublicData = createAsyncThunk<
  { vaultKey:VaultKey; value: SerializedCakeVault },
  { vaultKey:VaultKey; contractAddress: string; chainId: ChainId }
>(
  'flexible/fetchPublicData',
  async ({vaultKey, contractAddress, chainId}) => {
    const value = await fetchPublicFlexibleSideVaultData(chainId, contractAddress)
    return { vaultKey, value }
  },
)

export const fetchLockedVaultUserData = createAsyncThunk<
  { vaultKey:VaultKey; userData: SerializedLockedVaultUser },
  { vaultKey:VaultKey; contractAddress: string; account: string, chainId: ChainId }
>(
  'locked/fetchUser',
  async ({ account, chainId, vaultKey, contractAddress }) => {
    const userData = await fetchVaultUser(account, chainId, contractAddress)
    // console.log('fetchLockedVaultUserData', vaultKey, userData)
    return { vaultKey, userData }
  },
)

export const fetchFlexibleVaultUserData = createAsyncThunk<
  { vaultKey:VaultKey; userData: SerializedVaultUser },
  { vaultKey:VaultKey; contractAddress: string, account: string; chainId: ChainId }
>(
  'flexible/fetchUser',
  async ({ account, vaultKey, chainId, contractAddress }) => {
    const userData = await fetchFlexibleSideVaultUser(account, chainId, contractAddress)
    return { vaultKey, userData }
  },
)

export const fetchCakeVaultPublicData = createAsyncThunk<SerializedLockedCakeVault, {chainId: ChainId}>(
  'cakeVault/fetchPublicData',
  async ({chainId}) => {
    const publicVaultInfo = await fetchPublicVaultData(chainId)
    return publicVaultInfo
  },
)

export const fetchCakeFlexibleSideVaultPublicData = createAsyncThunk<SerializedCakeVault, {chainId: ChainId}>(
  'cakeFlexibleSideVault/fetchPublicData',
  async ({chainId}) => {
    const publicVaultInfo = await fetchPublicFlexibleSideVaultData(chainId)
    return publicVaultInfo
  },
)

export const fetchCakeVaultUserData = createAsyncThunk<SerializedLockedVaultUser, { account: string; chainId: ChainId }>(
  'cakeVault/fetchUser',
  async ({ account, chainId }) => {
    const userData = await fetchVaultUser(account, chainId)
    return userData
  },
)

export const fetchCakeFlexibleSideVaultUserData = createAsyncThunk<SerializedVaultUser, { account: string; chainId: ChainId }>(
  'cakeFlexibleSideVault/fetchUser',
  async ({ account, chainId }) => {
    const userData = await fetchFlexibleSideVaultUser(account, chainId)
    return userData
  },
)

export const fetchIfoPublicDataAsync = createAsyncThunk<PublicIfoData>('ifoVault/fetchIfoPublicDataAsync', async () => {
  const publicIfoData = await fetchPublicIfoData()
  return publicIfoData
})

export const fetchUserIfoCreditDataAsync = (account: string) => async (dispatch) => {
  try {
    const credit = await fetchUserIfoCredit(account)
    dispatch(setIfoUserCreditData(credit))
  } catch (error) {
    console.error('[Ifo Credit Action] Error fetching user Ifo credit data', error)
  }
}

export const PoolsSlice = createSlice({
  name: 'Pools',
  initialState,
  reducers: {
    setPoolPublicData: (state, action) => {
      const { sousId } = action.payload
      const poolIndex = state.data.findIndex((pool) => pool.sousId === sousId)
      state.data[poolIndex] = {
        ...state.data[poolIndex],
        ...action.payload.data,
      }
    },
    setPoolUserData: (state, action) => {
      const { sousId } = action.payload
      state.data = state.data.map((pool) => {
        if (pool.sousId === sousId) {
          return { ...pool, userDataLoaded: true, userData: action.payload.data }
        }
        return pool
      })
    },
    setPoolsPublicData: (state, action) => {
      const livePoolsData: SerializedPool[] = action.payload
      const livePoolsSousIdMap = keyBy(livePoolsData, 'sousId')
      state.data = state.data.map((pool) => {
        const livePoolData = livePoolsSousIdMap[pool.sousId]
        return { ...pool, ...livePoolData }
      })
    },
    // IFO
    setIfoUserCreditData: (state, action) => {
      const credit = action.payload
      state.ifo = { ...state.ifo, credit }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetUserState, (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state.data = state.data.map(({ userData, ...pool }) => {
        return { ...pool }
      })
      state.userDataLoaded = false
      state.cakeVault = { ...state.cakeVault, userData: initialPoolVaultState.userData }
      state.cakeFlexibleSideVault = { ...state.cakeFlexibleSideVault, userData: initialPoolVaultState.userData }
    })
    builder.addCase(
      fetchPoolsUserDataAsync.fulfilled,
      (
        state,
        action: PayloadAction<
          { sousId: number; allowance: any; stakingTokenBalance: any; stakedBalance: any; pendingReward: any }[]
        >,
      ) => {
        const userData = action.payload
        const userDataSousIdMap = keyBy(userData, 'sousId')
        state.data = state.data.map((pool) => ({
          ...pool,
          userDataLoaded: true,
          userData: userDataSousIdMap[pool.sousId],
        }))
        state.userDataLoaded = true
      },
    )
    builder.addCase(fetchPoolsUserDataAsync.rejected, (state, action) => {
      console.error('[Pools Action] Error fetching pool user data', action.payload)
    })
    // Vault public data that updates frequently
    builder.addCase(fetchCakeVaultPublicData.fulfilled, (state, action: PayloadAction<SerializedLockedCakeVault>) => {
      state.cakeVault = { ...state.cakeVault, ...action.payload }
    })
    builder.addCase(
      fetchCakeFlexibleSideVaultPublicData.fulfilled,
      (state, action: PayloadAction<SerializedCakeVault>) => {
        state.cakeFlexibleSideVault = { ...state.cakeFlexibleSideVault, ...action.payload }
      },
    )
    // Vault user data
    builder.addCase(fetchCakeVaultUserData.fulfilled, (state, action: PayloadAction<SerializedLockedVaultUser>) => {
      const userData = action.payload
      state.cakeVault = { ...state.cakeVault, userData }
    })
    // IFO
    builder.addCase(fetchIfoPublicDataAsync.fulfilled, (state, action: PayloadAction<PublicIfoData>) => {
      const { ceiling } = action.payload
      state.ifo = { ...state.ifo, ceiling }
    })
    builder.addCase(
      fetchCakeFlexibleSideVaultUserData.fulfilled,
      (state, action: PayloadAction<SerializedVaultUser>) => {
        const userData = action.payload
        state.cakeFlexibleSideVault = { ...state.cakeFlexibleSideVault, userData }
      },
    )
    builder.addMatcher(
      isAnyOf(
        updateUserAllowance.fulfilled,
        updateUserBalance.fulfilled,
        updateUserStakedBalance.fulfilled,
        updateUserPendingReward.fulfilled,
      ),
      (state, action: PayloadAction<{ sousId: number; field: string; value: any }>) => {
        const { field, value, sousId } = action.payload
        const index = state.data.findIndex((p) => p.sousId === sousId)

        if (index >= 0) {
          state.data[index] = { ...state.data[index], userData: { ...state.data[index].userData, [field]: value } }
        }
      },
    )
    builder.addMatcher(
      isAnyOf(
        fetchVaultPublicData.fulfilled,
      ),
      (state, action: PayloadAction<any>) => {
        for(const vaultKey in action.payload) {
          if(action.payload[vaultKey])
            state[vaultKey] = { ...initialPoolVaultState, ...state[vaultKey], ...action.payload[vaultKey] }
        }
      },
    )
    builder.addMatcher(
      isAnyOf(
        fetchVaultUserData.fulfilled
      ),
      (state, action: PayloadAction<any>) => {
        for(const vaultKey in action.payload) {
          if(action.payload[vaultKey])
            state[vaultKey] = { ...initialPoolVaultState, ...state[vaultKey], userData: action.payload[vaultKey] }
        }
      },
    )
    builder.addMatcher(
      isAnyOf(
        fetchVaultFeeDatas.fulfilled
      ),
      (state, action: PayloadAction<any>) => {
        for(const vaultKey in action.payload) {
          if(action.payload[vaultKey])
            state[vaultKey] = { ...initialPoolVaultState, ...state[vaultKey], fees: action.payload[vaultKey] }
        }
      },
    )
    builder.addMatcher(
      isAnyOf(
        fetchLockedVaultPublicData.fulfilled,
        fetchFlexibleVaultPublicData.fulfilled,
      ),
      (state, action: PayloadAction<{ vaultKey:VaultKey, value: any }>) => {
        const { vaultKey, value } = action.payload
        state[vaultKey] = { ...initialPoolVaultState, ...state[vaultKey], ...value }
      },
    )
  },
})

// Actions
export const { setPoolsPublicData, setPoolPublicData, setPoolUserData, setIfoUserCreditData } = PoolsSlice.actions

export default PoolsSlice.reducer
